import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { sanity, urlFor } from '../lib/sanity'

const placeholderImage = '/assets/images/placeholderEventImage.jpg'

const EVENT_QUERY = `*[_type == "event" && slug.current == $slug][0]{
  title,
  organiser,

  "type": coalesce(typeOption->title, type),
  "date": coalesce(dateOption->title, date),

  startDateTime,
  endDateTime,
  venueName,
  streetName,
  streetNumber,
  postalCode,
  city,

  "region": coalesce(regionOption->title, region),

  image,
  description,
  ticketText,
  ticketUrl,
  fundedByHerdenkingscomite
}`

function formatDate(dateString) {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateString))
}

function formatTime(dateString) {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateString))
}

function getDateParts(dateString) {
  const date = new Date(dateString)

  return {
    day: date.getDate(),
    month: new Intl.DateTimeFormat('nl-NL', {month: 'long'}).format(date),
    year: date.getFullYear(),
  }
}

function isSameDate(dateA, dateB) {
  if (!dateA || !dateB) return false

  const a = new Date(dateA)
  const b = new Date(dateB)

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatDateRange(startDateTime, endDateTime) {
  if (!startDateTime) return ''

  if (!endDateTime || isSameDate(startDateTime, endDateTime)) {
    return formatDate(startDateTime)
  }

  const start = getDateParts(startDateTime)
  const end = getDateParts(endDateTime)

  if (start.year !== end.year) {
    return `${start.day} ${start.month} ${start.year} - ${end.day} ${end.month} ${end.year}`
  }

  if (start.month === end.month) {
    return `${start.day} - ${end.day} ${end.month} ${end.year}`
  }

  return `${start.day} ${start.month} - ${end.day} ${end.month} ${end.year}`
}

function EventPage() {
  const { slug } = useParams()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)

  function formatDate(dateString) {
    if (!dateString) return ''
  
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(dateString))
  }
  
  function formatAddress(event) {
    return [
      event.venueName,
      [event.streetName, event.streetNumber].filter(Boolean).join(' '),
      [event.postalCode, event.city].filter(Boolean).join(' '),
      event.region,
    ]
      .filter(Boolean)
      .join('\n')
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    sanity
      .fetch(EVENT_QUERY, { slug })
      .then((result) => {
        setEvent(result)
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setLoading(false)
      })
  }, [slug])

  if (loading) return null

  if (!event) {
    return (
      <main className="min-h-screen text-[#5757fd] px-8 py-24">
        <p>Evenement niet gevonden.</p>
      </main>
    )
  }

  const hasSanityImage = event.image?.asset?._ref || event.image?.asset?._id

  const imageSrc = hasSanityImage
    ? urlFor(event.image).width(1920).url()
    : placeholderImage

    const buttonText = [
      'Raadpleeg de website van de organisatie voor deelname-informatie.',
      'Deelname is gratis. Aanmelden is niet nodig.',
    ].includes(event.ticketText)
      ? 'Meer informatie'
      : 'Meld je aan'

  return (
    <main className="min-h-screen text-[#5757fd]">
      <section className="relative">
      <div className="h-[460px] md:h-[560px] overflow-hidden">
  <img
    src={imageSrc}
    alt={event.image?.alt || event.title || 'Evenement afbeelding'}
    className="h-full w-full object-cover"
  />
</div>

        <div className="relative z-10 -mt-24 px-6 pb-10 md:-mt-28">
          <div className="mx-auto max-w-6xl rounded-[22px] bg-white px-6 py-12 shadow-sm md:px-12 md:py-14 lg:px-20">
            <div className="mx-auto max-w-3xl text-center">
              <p className="mb-6 text-lg font-sans">
              {[formatDateRange(event.startDateTime, event.endDateTime), event.city]
  .filter(Boolean)
  .join(' · ')}
              </p>

              <h1 className="text-4xl font-sans_bold leading-[0.95] tracking-tight md:text-5xl lg:text-6xl">
                {event.title}
              </h1>

              {event.fundedByHerdenkingscomite && (
  <div className="mx-auto mt-16 flex w-fit max-w-2xl items-center gap-3 rounded-xl border border-[#5757fd]/25 bg-[#f3f2ff]/50 px-2 py-2 text-left text-sm font-sans leading-tight text-[#5757fd] md:px-2 md:py-2 md:text-sm">
    <div
    className='bg-white p-4 rounded-xl shrink-0'>
    <img
      src="/assets/logos/HC_symbol_purple.svg"
      alt=""
      aria-hidden="true"
      className="h-7 w-auto shrink-0 md:h-8 "
    />
    </div>
    <span
    className='px-2'>
      Mede mogelijk gemaakt met financiering door het Herdenkingscomité
      Slavernijverleden
    </span>
  </div>
)}

              {event.description && (
                <div className="mx-auto mt-24 max-w-2xl">
                  <h2 className="mb-4 text-xl font-sans_bold">
                    Over dit evenement
                  </h2>

                  <p className="text-lg font-sans whitespace-pre-line">
                    {event.description}
                  </p>
                </div>
              )}
            </div>

            <div className="mx-auto mt-24 text-center justify-center max-w-2xl gap-8">
  <h3 className="mb-3 font-sans_bold tracking-wide">Locatie en tijd</h3>

  <div className="text-lg leading-tight">
    <p className="whitespace-pre-line">
      {formatAddress(event)}
    </p>

    <p className="mt-4">
  {formatDateRange(event.startDateTime, event.endDateTime)}
  <br />

  {event.endDateTime && !isSameDate(event.startDateTime, event.endDateTime) ? (
    <>
      Start: {formatDate(event.startDateTime)} om {formatTime(event.startDateTime)}
      <br />
      Eind: {formatDate(event.endDateTime)} om {formatTime(event.endDateTime)}
    </>
  ) : (
    <>
      Aanvang: {formatTime(event.startDateTime)}
      <br />
      {event.endDateTime && <>Eind: {formatTime(event.endDateTime)}</>}
    </>
  )}
</p>
  </div>
</div>

            <div className="mt-24 border-t-2 border-[#5757fd] pt-16 text-center">
              <h2 className="mb-3 text-xl font-sans_bold">Tickets</h2>

              {event.ticketText && (
                <p className="mb-9 text-lg font-sans">{event.ticketText}</p>
              )}

              {event.ticketUrl && (
                <Button
                  asChild
                  className="font-sans rounded-full bg-[#5757fd] px-8 py-6 text-lg text-white hover:bg-[#4444e8]"
                >
                  <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer">
  {buttonText}
</a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default EventPage